import {
    signOut as firebaseSignOut,
    onAuthStateChanged,
    signInWithEmailAndPassword,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../services/firebase';
import { USER_ROLES } from '../utils/constants';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // DEV ONLY: Check if a mock role is configured in localStorage
    const mockRole = localStorage.getItem('DEV_MOCK_ROLE');
    if (mockRole) {
      setCurrentUser({ uid: 'mock-uid', email: `mock-${mockRole}@smartbrain.ng` });
      setUserProfile({
        uid: 'mock-uid',
        fullName: `Mock ${mockRole.charAt(0).toUpperCase() + mockRole.slice(1)}`,
        email: `mock-${mockRole}@smartbrain.ng`,
        role: mockRole,
        isActive: true,
      });
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);
      setError(null);

      if (user) {
        try {
          // Fetch user profile from Firestore
          const userDoc = await getDoc(doc(db, 'users', user.uid));

          if (userDoc.exists()) {
            const userData = { uid: user.uid, ...userDoc.data() };

            // Check if user is admin, editor, or teacher
            if (userData.role !== USER_ROLES.ADMIN && userData.role !== USER_ROLES.EDITOR && userData.role !== USER_ROLES.TEACHER) {
              setError('Access denied. Admin, Editor, or Teacher privileges required.');
              await firebaseSignOut(auth);
              setCurrentUser(null);
              setUserProfile(null);
            } else {
              setCurrentUser(user);
              setUserProfile(userData);
            }
          } else {
            setError('User profile not found.');
            await firebaseSignOut(auth);
            setCurrentUser(null);
            setUserProfile(null);
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
          setError('Failed to load user profile.');
          setCurrentUser(null);
          setUserProfile(null);
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signIn = async (email, password, rememberMe = false) => {
    setError(null);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Store remember me preference
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      }

      return userCredential.user;
    } catch (err) {
      console.error('Sign in error:', err);
      let errorMessage = 'Failed to sign in. Please try again.';

      if (err.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password.';
      } else if (err.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const signOut = async () => {
    setError(null);
    try {
      await firebaseSignOut(auth);
      localStorage.removeItem('rememberMe');
      setCurrentUser(null);
      setUserProfile(null);
    } catch (err) {
      console.error('Sign out error:', err);
      setError('Failed to sign out.');
      throw err;
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    error,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
